import { Suspense, useEffect, useRef, useState } from "react";

/**
 * Renders `children` (a lazy component) only after the wrapper crosses
 * the viewport. Prevents fetching the chunk for below-fold landing
 * sections until the visitor actually scrolls toward them.
 *
 * Uses IntersectionObserver with a 200px rootMargin so the chunk starts
 * downloading a hair before the section enters — the user rarely sees
 * the fallback skeleton.
 *
 * Falls back to eager render on browsers without IntersectionObserver
 * (SSR, ancient Safari) — those are rare and eating the extra bytes
 * once is fine.
 */
export default function LazyOnVisible({ children, minHeight = 320, fallback = null, rootMargin = "200px" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setVisible(true);
          io.disconnect();
          break;
        }
      }
    }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [visible, rootMargin]);

  return (
    <div ref={ref} style={{ minHeight: visible ? undefined : minHeight }}>
      {visible ? <Suspense fallback={fallback}>{children}</Suspense> : null}
    </div>
  );
}
